// ViewPartnerData.tsx
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Calendar,
  Trophy,
  CreditCard,
  FileBadge,
  Map,
  Zap,
  Droplets,
  Leaf,
  Recycle,
  Handshake,
  ShieldCheck,
  Factory,
} from "lucide-react";

import {
  searchOrganizations,
  getPartnerData,
  getSustainabilityRoadmap,
  deleteSummaryMetric,
  type OrgOption,
} from "../data-entry/utils";

// If you use recharts:
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

export const ViewPartnerData: React.FC = () => {
  const [orgQuery, setOrgQuery] = useState("");
  const [orgOptions, setOrgOptions] = useState<OrgOption[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OrgOption | null>(null);
  const [isOrgLoading, setIsOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);

  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  type RangePreset =
    | "last_month"
    | "last_3_months"
    | "last_year"
    | "lifetime"
    | null;

  const [activePreset, setActivePreset] = useState<RangePreset>(null);

  const [roadmap, setRoadmap] = useState<any>(null);
  const [deletingRowId, setDeletingRowId] = useState<number | null>(null);
  const SUMMARY_ROWS_PER_PAGE = 15;
  const [summaryPage, setSummaryPage] = useState(1);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // close dropdown
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target as Node)) setOrgOptions([]);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // debounced org search
  useEffect(() => {
    const q = orgQuery.trim();

    if (q.length < 2) {
      setOrgOptions([]);
      setOrgError(null);
      return;
    }

    if (
      selectedOrg &&
      q.toLowerCase() === selectedOrg.organizationName.trim().toLowerCase()
    ) {
      setOrgOptions([]);
      setOrgError(null);
      return;
    }

    const t = setTimeout(async () => {
      setIsOrgLoading(true);
      setOrgError(null);

      const resp = await searchOrganizations(q, 8);

      if (resp.status === "error") {
        setOrgOptions([]);
        setOrgError(resp.message || "Failed to fetch organizations");
      } else {
        setOrgOptions(resp.data || []);
      }

      setIsOrgLoading(false);
    }, 300);

    return () => clearTimeout(t);
  }, [orgQuery, selectedOrg]);

  const selectOrganization = (opt: OrgOption) => {
    setSelectedOrg(opt);
    setOrgQuery(opt.organizationName);
    setOrgOptions([]);
  };

  // clear selection if user edits text
  useEffect(() => {
    if (!selectedOrg) return;
    if (orgQuery.trim() !== selectedOrg.organizationName) setSelectedOrg(null);
  }, [orgQuery]);

  const fetchPartner = async () => {
    setError(null);
    setData(null);
    setRoadmap(null);
    setSummaryPage(1);

    if (!selectedOrg?.organizationId) {
      setError("Please select an organization.");
      return;
    }

    setLoading(true);
    try {
      const resp = await getPartnerData(
        selectedOrg.organizationId,
        fromDate || undefined,
        toDate || undefined,
      );
      if (resp.status === "error") throw new Error(resp.message);
      setData(resp.data);

      const partnerAccountId = resp?.data?.partner?.accountId;
      if (partnerAccountId) {
        const roadmapResp = await getSustainabilityRoadmap(partnerAccountId);
        if (roadmapResp.status === "success") {
          setRoadmap(roadmapResp.data);
        }
      }

      console.log("VIEW DATA", resp.data);
    } catch (e: any) {
      setError(e?.message || "Failed to fetch partner data.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSummaryRow = async (rowId: number) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this summary metric row?",
    );
    if (!confirmed) return;

    setDeletingRowId(rowId);
    setError(null);

    try {
      const resp = await deleteSummaryMetric(rowId);
      if (resp.status === "error") {
        throw new Error(resp.message || "Failed to delete row");
      }

      await fetchPartner();
    } catch (e: any) {
      setError(e?.message || "Failed to delete summary metric row.");
    } finally {
      setDeletingRowId(null);
    }
  };
  const monthly = data?.summaryMetrics?.monthly || [];
  const certs = data?.certificates || [];
  const rows = data?.summaryMetrics?.rows || [];

  const totalSummaryPages = Math.max(
    1,
    Math.ceil(rows.length / SUMMARY_ROWS_PER_PAGE),
  );

  const paginatedRows = rows.slice(
    (summaryPage - 1) * SUMMARY_ROWS_PER_PAGE,
    summaryPage * SUMMARY_ROWS_PER_PAGE,
  );

  const summaryStartEntry =
    rows.length === 0 ? 0 : (summaryPage - 1) * SUMMARY_ROWS_PER_PAGE + 1;

  const summaryEndEntry = Math.min(
    summaryPage * SUMMARY_ROWS_PER_PAGE,
    rows.length,
  );

  const offsettingValue = Number(
    data?.partner?.impactTotals?.offsetting_value || 0,
  );
  const emissionValue = Number(
    data?.partner?.impactTotals?.emission_value || 0,
  );
  const roadmapAreas = roadmap?.priority_areas || [];

  useEffect(() => {
    if (summaryPage > totalSummaryPages) {
      setSummaryPage(totalSummaryPages);
    }
  }, [summaryPage, totalSummaryPages]);

  const getRoadmapMeta = (priorityKey: string) => {
    switch (priorityKey) {
      case "energy_emissions":
        return {
          title: "Energy and emissions",
          currentLabel: "Current emissions",
          icon: <Zap size={16} />,
        };
      case "water_management":
        return {
          title: "Water Management",
          currentLabel: "Current consumption",
          icon: <Droplets size={16} />,
        };
      case "nature_stewardship":
        return {
          title: "Nature Stewardship",
          currentLabel: "Currently supporting",
          icon: <Leaf size={16} />,
        };
      case "waste_management":
        return {
          title: "Waste Management",
          currentLabel: "Current",
          icon: <Recycle size={16} />,
        };
      case "land_governance":
        return {
          title: "Land Governance",
          currentLabel: "Current",
          icon: <Handshake size={16} />,
        };
      default:
        return {
          title: priorityKey,
          currentLabel: "Current",
          icon: <Map size={16} />,
        };
    }
  };
  const formatMetricValue = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatDateInput = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const applyRangePreset = (preset: Exclude<RangePreset, null>) => {
    const today = new Date();
    let nextFrom = "";
    let nextTo = formatDateInput(today);

    if (preset === "last_month") {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      nextFrom = formatDateInput(start);
      nextTo = formatDateInput(end);
    }

    if (preset === "last_3_months") {
      const start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
      nextFrom = formatDateInput(start);
    }

    if (preset === "last_year") {
      const start = new Date(
        today.getFullYear() - 1,
        today.getMonth(),
        today.getDate(),
      );
      nextFrom = formatDateInput(start);
    }

    if (preset === "lifetime") {
      nextFrom = "";
      nextTo = "";
    }

    setFromDate(nextFrom);
    setToDate(nextTo);
    setActivePreset(preset);
  };
  return (
    <Page>
      <Main>
        <Header>
          <H1>View Partner Data</H1>
          <Sub>
            Search an organization and view account, payments, certificates, and
            summary metrics.
          </Sub>
        </Header>

        <ControlsCard>
          <Row>
            <Field>
              <Label>Partner (Organization)</Label>
              <OrgDropdown ref={dropdownRef}>
                <OrgInput
                  placeholder="Search organization (min 2 letters)"
                  value={orgQuery}
                  onChange={(e) => setOrgQuery(e.target.value)}
                />
                {isOrgLoading && <Hint>Searching...</Hint>}
                {!!orgError && (
                  <Hint style={{ color: "#b91c1c" }}>{orgError}</Hint>
                )}

                {!isOrgLoading &&
                  !orgError &&
                  orgQuery.trim().length >= 2 &&
                  orgOptions.length > 0 && (
                    <OrgMenu>
                      {orgOptions.map((opt) => (
                        <OrgItem
                          key={opt.organizationId}
                          onClick={() => selectOrganization(opt)}
                        >
                          {opt.organizationName}
                        </OrgItem>
                      ))}
                    </OrgMenu>
                  )}

                {selectedOrg && (
                  <SelectedTag>
                    Selected: {selectedOrg.organizationName}
                  </SelectedTag>
                )}
              </OrgDropdown>
            </Field>

            <Field>
              <Label>
                <Calendar size={14} /> From
              </Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setActivePreset(null);
                }}
              />
            </Field>

            <Field>
              <Label>
                <Calendar size={14} /> To
              </Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setActivePreset(null);
                }}
              />
            </Field>

            <FetchButton onClick={fetchPartner} disabled={loading}>
              {loading ? "Loading..." : "Fetch"}
            </FetchButton>
          </Row>
          <RangePresetRow>
            <RangePresetLabel>Quick ranges</RangePresetLabel>

            <RangePresetChips>
              <RangeChip
                type="button"
                $active={activePreset === "last_month"}
                onClick={() => applyRangePreset("last_month")}
              >
                Last month
              </RangeChip>

              <RangeChip
                type="button"
                $active={activePreset === "last_3_months"}
                onClick={() => applyRangePreset("last_3_months")}
              >
                Last 3 months
              </RangeChip>

              <RangeChip
                type="button"
                $active={activePreset === "last_year"}
                onClick={() => applyRangePreset("last_year")}
              >
                Last year
              </RangeChip>

              <RangeChip
                type="button"
                $active={activePreset === "lifetime"}
                onClick={() => applyRangePreset("lifetime")}
              >
                Overall
              </RangeChip>
            </RangePresetChips>
          </RangePresetRow>
          {error && <ErrorBanner>{error}</ErrorBanner>}
        </ControlsCard>

        <AnimatePresence mode="wait">
          {data && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {/* Profile */}
              <Grid2>
                <Card>
                  <CardTitle>
                    <Search size={16} /> Partner Profile
                  </CardTitle>
                  <ProfileRow>
                    <Avatar
                      src={data?.partner?.account?.profilePicture || ""}
                      alt="profile"
                      onError={(e: any) =>
                        (e.currentTarget.style.display = "none")
                      }
                    />
                    <div>
                      <BigName>
                        {(data?.partner?.account?.firstName || "") +
                          " " +
                          (data?.partner?.account?.lastName || "")}
                      </BigName>
                      <SmallText>
                        {data?.partner?.account?.email || "-"}
                      </SmallText>
                      <SmallText>
                        {data?.partner?.account?.phoneNumber || "-"}
                      </SmallText>
                      <SmallText style={{ marginTop: 8, opacity: 0.9 }}>
                        <span style={{ fontWeight: 800 }}>Organization:</span>{" "}
                        {data?.organization?.organizationName}
                      </SmallText>
                    </div>
                  </ProfileRow>

                  <Divider />

                  <CardTitle>
                    <CreditCard size={16} /> Stripe
                  </CardTitle>
                  <SmallText>
                    stripeId: {data?.partner?.stripe?.stripeId || "-"}
                  </SmallText>
                  <SmallText>
                    currency: {data?.partner?.stripe?.currency || "-"}
                  </SmallText>
                </Card>

                <Card>
                  <CardTitle>
                    <Trophy size={16} /> Rankings (by Invested in Nature)
                  </CardTitle>

                  <RankRow>
                    <RankBox>
                      <RankLabel>Overall Rank</RankLabel>
                      <RankValue>
                        {data?.partner?.rankings?.overall?.rank ?? "-"}
                      </RankValue>
                      <RankSub>
                        Total:{" "}
                        {data?.partner?.rankings?.overall?.invested_sum ?? "-"}
                      </RankSub>
                    </RankBox>

                    <RankBox>
                      <RankLabel>Range Rank</RankLabel>
                      <RankValue>
                        {data?.partner?.rankings?.period?.rank ?? "-"}
                      </RankValue>
                      <RankSub>
                        Total:{" "}
                        {data?.partner?.rankings?.period?.invested_sum ?? "-"}
                      </RankSub>
                    </RankBox>
                  </RankRow>

                  <Note>
                    Ranking is computed across all partners using
                    sum(invested_in_nature).
                  </Note>
                </Card>
              </Grid2>

              <Grid2 style={{ marginTop: 14 }}>
                <Card>
                  <CardTitle>
                    <ShieldCheck size={16} /> Offsetting Value
                  </CardTitle>

                  <MetricSummaryBox>
                    <MetricSummaryLabel>Selected Range</MetricSummaryLabel>
                    <MetricSummaryValue>
                      {formatMetricValue(offsettingValue)}
                    </MetricSummaryValue>
                    <MetricSummarySub>
                      Sum of compensated values shown as absolute.
                    </MetricSummarySub>
                  </MetricSummaryBox>
                </Card>

                <Card>
                  <CardTitle>
                    <Factory size={16} /> Emission Value
                  </CardTitle>

                  <MetricSummaryBox>
                    <MetricSummaryLabel>Selected Range</MetricSummaryLabel>
                    <MetricSummaryValue>
                      {formatMetricValue(emissionValue)}
                    </MetricSummaryValue>
                    <MetricSummarySub>
                      Sum of total_emission for selected date range.
                    </MetricSummarySub>
                  </MetricSummaryBox>
                </Card>
              </Grid2>
              {/* Charts */}
              <Grid2 style={{ marginTop: 14 }}>
                <Card>
                  <CardTitle>Monthly Invested in Nature</CardTitle>
                  <ChartWrap>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart
                        data={monthly.map((m: any) => ({
                          month: String(m.month).slice(0, 7),
                          invested: Number(m.invested_sum || 0),
                        }))}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <Tooltip
                          contentStyle={{
                            background: "#fff",
                            borderRadius: 8,
                            border: "1px solid #E5E7EB",
                            fontWeight: 700,
                          }}
                        />{" "}
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Bar
                          dataKey="invested"
                          fill="#6FAE3B"
                          radius={[6, 6, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartWrap>
                </Card>

                <Card>
                  <CardTitle>Monthly Total Emission Till Now</CardTitle>
                  <ChartWrap>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart
                        data={monthly.map((m: any) => ({
                          month: String(m.month).slice(0, 7),
                          emission: Number(m.emission_sum || 0),
                        }))}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <Tooltip
                          contentStyle={{
                            background: "#fff",
                            borderRadius: 8,
                            border: "1px solid #E5E7EB",
                            fontWeight: 700,
                          }}
                        />{" "}
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Bar
                          dataKey="emission"
                          fill="#6B7280"
                          radius={[6, 6, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartWrap>
                </Card>
              </Grid2>

              {/* Certificates */}
              <Card style={{ marginTop: 14 }}>
                <CardTitle>
                  <FileBadge size={16} /> Certificates
                </CardTitle>
                {certs.length === 0 ? (
                  <Empty> No certificate history found. </Empty>
                ) : (
                  <TableWrap>
                    <Table>
                      <thead>
                        <tr>
                          <th>Issuance date</th>
                          <th>Certificate code</th>
                          <th>Amount paid</th>
                          <th>Certificate URL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {certs.map((c: any, idx: number) => (
                          <tr key={idx}>
                            <td>
                              {c.issuance_date
                                ? String(c.issuance_date).slice(0, 10)
                                : "-"}
                            </td>
                            <td>{c.certificate_code || "-"}</td>
                            <td>{c.amount_paid ?? "-"}</td>
                            <td
                              style={{
                                maxWidth: 360,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {c.certificate_url ? (
                                <a
                                  href={c.certificate_url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Open
                                </a>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                )}
              </Card>

              {/* Summary metrics table */}
              <Card style={{ marginTop: 14 }}>
                <CardTitle>Summary Metrics</CardTitle>
                {/* {rows.length === 0 ? (
                  <Empty>No summary metrics found for selected range.</Empty>
                ) : (
                  <TableWrap>
                    <WideTable>
                      <thead>
                        <tr>
                          <StickyTh>Date</StickyTh>
                          <th>Quarter</th>
                          <th>Year</th>
                          <th>Source</th>
                          <th>Category</th>
                          <th>Sub category</th>
                          <th>Invested</th>
                          <th>Total emission</th>
                          <th>Compensated</th>
                          <th>Net impact</th>
                          <th>Type</th>
                          <th>Landscape</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedRows.map((r: any) => (
                          <tr key={r.id}>
                            <StickyTd>{String(r.date).slice(0, 10)}</StickyTd>
                            <td>{r.quater ?? r.quarter}</td>
                            <td>{r.year}</td>
                            <td>{r.source_of_information}</td>
                            <td>{r.category}</td>
                            <td>{r.sub_category}</td>
                            <td>{r.invested_in_nature}</td>
                            <td>{r.total_emmission_till_now}</td>
                            <td>{r.compensated}</td>
                            <td>{r.net_impact}</td>
                            <td>{r.type_of_partner}</td>
                            <td>{r.landscape_id}</td>
                            <td>
                              <DeleteBtn
                                onClick={() => handleDeleteSummaryRow(r.id)}
                                disabled={deletingRowId === r.id}
                                title="Delete row"
                              >
                                {deletingRowId === r.id ? "..." : "✕"}
                              </DeleteBtn>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </WideTable>
                  </TableWrap>
                )} */}
                {rows.length === 0 ? (
                  <Empty>No summary metrics found for selected range.</Empty>
                ) : (
                  <>
                    <TableWrap>
                      <WideTable>
                        <thead>
                          <tr>
                            <StickyTh>Date</StickyTh>
                            <th>Quarter</th>
                            <th>Year</th>
                            <th>Source</th>
                            <th>Category</th>
                            <th>Sub category</th>
                            <th>Invested</th>
                            <th>Total emission</th>
                            <th>Compensated</th>
                            <th>Net impact</th>
                            <th>Type</th>
                            <th>Landscape</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedRows.map((r: any) => (
                            <tr key={r.id}>
                              <StickyTd>{String(r.date).slice(0, 10)}</StickyTd>
                              <td>{r.quater ?? r.quarter}</td>
                              <td>{r.year}</td>
                              <td>{r.source_of_information}</td>
                              <td>{r.category}</td>
                              <td>{r.sub_category}</td>
                              <td>{r.invested_in_nature}</td>
                              <td>{r.total_emmission_till_now}</td>
                              <td>{r.compensated}</td>
                              <td>{r.net_impact}</td>
                              <td>{r.type_of_partner}</td>
                              <td>{r.landscape_id}</td>
                              <td>
                                <DeleteBtn
                                  onClick={() => handleDeleteSummaryRow(r.id)}
                                  disabled={deletingRowId === r.id}
                                  title="Delete row"
                                >
                                  {deletingRowId === r.id ? "..." : "✕"}
                                </DeleteBtn>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </WideTable>
                    </TableWrap>

                    <PaginationWrap>
                      <PaginationInfo>
                        Showing {summaryStartEntry}-{summaryEndEntry} of{" "}
                        {rows.length} entries
                      </PaginationInfo>

                      <PaginationControls>
                        <PageButton
                          type="button"
                          onClick={() =>
                            setSummaryPage((p) => Math.max(1, p - 1))
                          }
                          disabled={summaryPage === 1}
                        >
                          Previous
                        </PageButton>

                        <PageIndicator>
                          Page {summaryPage} of {totalSummaryPages}
                        </PageIndicator>

                        <PageButton
                          type="button"
                          onClick={() =>
                            setSummaryPage((p) =>
                              Math.min(totalSummaryPages, p + 1),
                            )
                          }
                          disabled={summaryPage === totalSummaryPages}
                        >
                          Next
                        </PageButton>
                      </PaginationControls>
                    </PaginationWrap>
                  </>
                )}
              </Card>
              <Card style={{ marginTop: 14 }}>
                <CardTitle>
                  <Map size={16} /> Sustainability Roadmap
                </CardTitle>

                {!roadmap ? (
                  <Empty>
                    No sustainability roadmap found for this partner.
                  </Empty>
                ) : (
                  <>
                    <RoadmapHeaderCard>
                      <RoadmapHeaderGrid>
                        <RoadmapMiniBox>
                          <RoadmapMiniLabel>Baseline Year</RoadmapMiniLabel>
                          <RoadmapMiniValue>
                            {roadmap.baseline_year}
                          </RoadmapMiniValue>
                        </RoadmapMiniBox>

                        <RoadmapMiniBox>
                          <RoadmapMiniLabel>Target Year</RoadmapMiniLabel>
                          <RoadmapMiniValue>
                            {roadmap.target_year}
                          </RoadmapMiniValue>
                        </RoadmapMiniBox>

                        <RoadmapMiniBox>
                          <RoadmapMiniLabel>Last Review</RoadmapMiniLabel>
                          <RoadmapMiniValue>
                            {roadmap.updated_at
                              ? String(roadmap.updated_at).slice(0, 10)
                              : "-"}
                          </RoadmapMiniValue>
                        </RoadmapMiniBox>
                      </RoadmapHeaderGrid>
                    </RoadmapHeaderCard>

                    <RoadmapGrid>
                      {roadmapAreas.map((area: any) => {
                        const meta = getRoadmapMeta(area.priority_key);

                        return (
                          <RoadmapAreaCard key={area.id}>
                            <RoadmapAreaHead>
                              <RoadmapAreaIcon>{meta.icon}</RoadmapAreaIcon>
                              <RoadmapAreaTitleWrap>
                                <RoadmapAreaTitle>
                                  {meta.title}
                                </RoadmapAreaTitle>
                                <RoadmapAreaSub>
                                  {area.priority_key}
                                </RoadmapAreaSub>
                              </RoadmapAreaTitleWrap>
                            </RoadmapAreaHead>

                            <MetricList>
                              <MetricRow>
                                <MetricLabel>
                                  {roadmap.baseline_year} baseline
                                </MetricLabel>
                                <MetricValue>
                                  {area.baseline_value ?? "-"}
                                </MetricValue>
                              </MetricRow>

                              <MetricRow>
                                <MetricLabel>
                                  {roadmap.target_year} target
                                </MetricLabel>
                                <MetricValue>
                                  {area.target_value ?? "-"}
                                </MetricValue>
                              </MetricRow>

                              <MetricRow>
                                <MetricLabel>{meta.currentLabel}</MetricLabel>
                                <MetricValue>
                                  {area.current_value ?? "-"}
                                </MetricValue>
                              </MetricRow>

                              {area.secondary_label && (
                                <MetricRow>
                                  <MetricLabel>
                                    {area.secondary_label}
                                  </MetricLabel>
                                  <MetricValue>
                                    {area.secondary_value ?? "-"}
                                  </MetricValue>
                                </MetricRow>
                              )}
                            </MetricList>
                          </RoadmapAreaCard>
                        );
                      })}
                    </RoadmapGrid>
                  </>
                )}
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </Main>
    </Page>
  );
};

/* styles */
const Page = styled.div`
  min-height: 100vh;
  background: var(--background);
`;
const Main = styled.main`
  padding: 28px 32px 60px;
`;
const Header = styled.div`
  text-align: center;
  margin-bottom: 16px;
`;
const H1 = styled.h1`
  margin: 0;
  color: var(--trust-green);
`;
const Sub = styled.p`
  margin: 6px 0 0;
  color: rgba(0, 77, 55, 0.55);
`;

const ControlsCard = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 14px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr auto;
  gap: 12px;
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div``;
const Label = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: var(--trust-green);
  margin-bottom: 6px;
  display: flex;
  gap: 6px;
  align-items: center;
`;
const Input = styled.input`
  width: 100%;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--card);
  color: var(--trust-green);
  padding: 0 12px;
`;

const FetchButton = styled.button`
  height: 40px;
  border-radius: 12px;
  border: none;
  padding: 0 18px;
  font-weight: 800;
  color: #fff;
  background: var(--action-orange);
  cursor: pointer;
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const ErrorBanner = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(185, 28, 28, 0.25);
  background: rgba(185, 28, 28, 0.06);
  color: #7f1d1d;
  font-weight: 700;
  font-size: 13px;
`;

const OrgDropdown = styled.div`
  position: relative;
`;
const OrgInput = styled(Input)``;
const Hint = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: rgba(0, 77, 55, 0.55);
`;
const OrgMenu = styled.div`
  position: absolute;
  z-index: 20;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--card);
  overflow: hidden;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.08);
`;
const OrgItem = styled.button`
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  &:hover {
    background: rgba(222, 245, 186, 0.25);
  }
`;
const SelectedTag = styled.div`
  margin-top: 8px;
  display: inline-block;
  font-size: 12px;
  font-weight: 800;
  color: var(--trust-green);
  background: rgba(222, 245, 186, 0.35);
  border: 1px solid rgba(117, 172, 56, 0.25);
  padding: 4px 8px;
  border-radius: 999px;
`;

const Grid2 = styled.div`
  max-width: 1200px;
  margin: 14px auto 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 14px;
`;
const CardTitle = styled.div`
  font-weight: 900;
  color: var(--trust-green);
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;
const ProfileRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;
const Avatar = styled.img`
  width: 58px;
  height: 58px;
  border-radius: 14px;
  object-fit: cover;
  background: rgba(0, 0, 0, 0.06);
`;
const BigName = styled.div`
  font-weight: 900;
  color: var(--trust-green);
  font-size: 16px;
`;
const SmallText = styled.div`
  color: rgba(0, 77, 55, 0.65);
  font-weight: 700;
  font-size: 12px;
`;
const Divider = styled.div`
  height: 1px;
  background: var(--border);
  margin: 12px 0;
`;

const RankRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;
const RankBox = styled.div`
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px;
  background: rgba(222, 245, 186, 0.16);
`;
const RankLabel = styled.div`
  font-size: 12px;
  font-weight: 900;
  color: rgba(0, 77, 55, 0.7);
`;
const RankValue = styled.div`
  font-size: 28px;
  font-weight: 1000;
  color: var(--trust-green);
  line-height: 1.1;
  margin-top: 6px;
`;
const RankSub = styled.div`
  margin-top: 6px;
  font-size: 12px;
  font-weight: 800;
  color: rgba(0, 77, 55, 0.65);
`;
const Note = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: rgba(0, 77, 55, 0.55);
  font-weight: 700;
`;

const ChartWrap = styled.div`
  width: 100%;
  height: 260px;
`;

const TableWrap = styled.div`
  overflow: auto;
  border-radius: 12px;
  border: 1px solid var(--border);
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 10px 12px;
    font-size: 12px;
    border-bottom: 1px solid var(--border);
  }
  th {
    text-align: left;
    color: rgba(0, 77, 55, 0.75);
    font-weight: 900;
    background: rgba(239, 231, 218, 0.35);
  }
  td {
    color: rgba(0, 77, 55, 0.75);
    font-weight: 700;
  }
`;
const WideTable = styled(Table)`
  min-width: 1200px;
`;
const StickyTh = styled.th`
  position: sticky;
  left: 0;
  z-index: 2;
  background: rgba(239, 231, 218, 0.95);
`;
const StickyTd = styled.td`
  position: sticky;
  left: 0;
  z-index: 1;
  background: var(--card);
`;

const Empty = styled.div`
  padding: 12px;
  color: rgba(0, 77, 55, 0.55);
  font-weight: 800;
`;
const RoadmapHeaderCard = styled.div`
  margin-bottom: 14px;
  border: 1px solid rgba(117, 172, 56, 0.18);
  background: linear-gradient(
    135deg,
    rgba(222, 245, 186, 0.22),
    rgba(239, 231, 218, 0.45)
  );
  border-radius: 14px;
  padding: 14px;
`;

const RoadmapHeaderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const RoadmapMiniBox = styled.div`
  background: rgba(255, 255, 255, 0.62);
  border: 1px solid rgba(0, 77, 55, 0.08);
  border-radius: 12px;
  padding: 12px;
`;

const RoadmapMiniLabel = styled.div`
  font-size: 11px;
  font-weight: 800;
  color: rgba(0, 77, 55, 0.55);
  margin-bottom: 4px;
`;

const RoadmapMiniValue = styled.div`
  font-size: 20px;
  font-weight: 900;
  color: var(--trust-green);
`;

const RoadmapGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const RoadmapAreaCard = styled.div`
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.55);
`;

const RoadmapAreaHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const RoadmapAreaIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: rgba(222, 245, 186, 0.32);
  color: var(--trust-green);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RoadmapAreaTitleWrap = styled.div``;

const RoadmapAreaTitle = styled.div`
  font-size: 14px;
  font-weight: 900;
  color: var(--trust-green);
`;

const RoadmapAreaSub = styled.div`
  font-size: 11px;
  color: rgba(0, 77, 55, 0.5);
  font-weight: 700;
  margin-top: 2px;
`;

const MetricList = styled.div`
  display: grid;
  gap: 10px;
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(239, 231, 218, 0.32);
`;

const MetricLabel = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: rgba(0, 77, 55, 0.65);
`;

const MetricValue = styled.div`
  font-size: 13px;
  font-weight: 900;
  color: var(--trust-green);
  text-align: right;
`;
const DeleteBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(185, 28, 28, 0.25);
  background: rgba(185, 28, 28, 0.08);
  color: #b91c1c;
  font-size: 16px;
  font-weight: 900;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(185, 28, 28, 0.14);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
const MetricSummaryBox = styled.div`
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 16px;
  background: rgba(222, 245, 186, 0.1);
`;

const MetricSummaryLabel = styled.div`
  font-size: 12px;
  font-weight: 900;
  color: rgba(0, 77, 55, 0.7);
`;

const MetricSummaryValue = styled.div`
  margin-top: 8px;
  font-size: 30px;
  line-height: 1.1;
  font-weight: 1000;
  color: var(--trust-green);
`;

const MetricSummarySub = styled.div`
  margin-top: 8px;
  font-size: 12px;
  font-weight: 700;
  color: rgba(0, 77, 55, 0.55);
`;
const RangePresetRow = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 900px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const RangePresetLabel = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: rgba(0, 77, 55, 0.7);
`;

const RangePresetChips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const RangeChip = styled.button<{ $active: boolean }>`
  height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid
    ${({ $active }) => ($active ? "rgba(117, 172, 56, 0.45)" : "var(--border)")};
  background: ${({ $active }) =>
    $active ? "rgba(222, 245, 186, 0.45)" : "var(--card)"};
  color: var(--trust-green);
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(222, 245, 186, 0.28);
  }
`;
const PaginationWrap = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const PaginationInfo = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: rgba(0, 77, 55, 0.6);
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const PageButton = styled.button`
  min-width: 92px;
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--card);
  color: var(--trust-green);
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(222, 245, 186, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageIndicator = styled.div`
  min-width: 110px;
  text-align: center;
  font-size: 12px;
  font-weight: 900;
  color: var(--trust-green);
`;
